/**
 * Ensure HQ admin API calls use HttpOnly cookies + portal header (no localStorage JWT).
 */
import { PORTAL_HEADER } from './storage';

const PORTAL_PATH_RE = /\/enterprise\/(workspace|partner|executive|salon-admin)\b/;

function detectPortalFromLocation() {
  if (typeof window === 'undefined') return '';
  const match = String(window.location.pathname || '').match(PORTAL_PATH_RE);
  if (!match) return '';
  const segment = match[1];
  if (segment === 'salon-admin') return 'workspace';
  return segment;
}

function isAdminApiUrl(url) {
  const s = String(url || '');
  if (!s.startsWith('http') && !s.startsWith('/')) return false;
  if (
    s.includes('/enterprise-portal-api') ||
    s.includes('/nyafaz-portal-api') ||
    s.includes('/naturals-portal-api')
  ) {
    return false;
  }
  // Salon video API uses X-Salon-Id — do not inject portal admin headers/credentials.
  if (s.includes('/api/salon-video')) return false;
  return (
    s.includes('/api/') ||
    s.includes('/users') ||
    s.includes('/payment') ||
    s.includes('/checkin-queue') ||
    s.includes('/franchise-flow') ||
    s.includes('/organization') ||
    s.includes('/enterprise') ||
    s.includes('/all-modules') ||
    s.includes('/plan-') ||
    s.includes('/modules') ||
    s.includes('/contact-messages') ||
    s.includes('/makeup-catalog') ||
    s.includes('/subscriptions') ||
    s.includes('/invoices') ||
    s.includes('/billing') ||
    s.includes('-salon-admin-api') ||
    s.includes('import-catalog-services') ||
    s.includes('purge-default-catalog-services')
  );
}

function cleanAuthorization(headers) {
  const auth = headers.get('Authorization') || headers.get('authorization') || '';
  if (/^Bearer\s*(null|undefined)?\s*$/i.test(auth.trim())) {
    headers.delete('Authorization');
    headers.delete('authorization');
  }
}

export function installPortalFetchInterceptor() {
  if (typeof window === 'undefined' || window.__enterpriseFetchInterceptorInstalled) return;
  window.__enterpriseFetchInterceptorInstalled = true;

  const nativeFetch = window.fetch.bind(window);

  window.fetch = function portalAwareFetch(input, init = {}) {
    const url = typeof input === 'string' ? input : input?.url || '';
    if (!isAdminApiUrl(url)) {
      return nativeFetch(input, init);
    }

    const nextInit = { ...init, credentials: init.credentials ?? 'include' };
    const headers = new Headers(nextInit.headers || {});
    cleanAuthorization(headers);

    const portal = detectPortalFromLocation();
    if (portal && !headers.has(PORTAL_HEADER)) {
      headers.set(PORTAL_HEADER, portal);
    }
    if (!headers.has('Content-Type') && nextInit.body && typeof nextInit.body === 'string') {
      headers.set('Content-Type', 'application/json');
    }
    // Never set Content-Type on FormData — browser must set multipart boundary.
    if (typeof FormData !== 'undefined' && nextInit.body instanceof FormData) {
      headers.delete('Content-Type');
    }
    nextInit.headers = headers;
    return nativeFetch(input, nextInit);
  };
}
